import { Record as record, Set as set } from 'immutable';
import _ from 'lodash';

import config from '../config';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import trainTime from '../lib/TrainTime';
import Station from './Station';
import CustomDuration from './CustomDuration';
import OrderFare from './order/Fare';
import Favourite from './Favourite';
import { PassengerTypes, DurationTypes } from '../constants/AppConstants';
import { construct, resolve } from '../helpers/LinkMapper';
import SearchMixin from './SearchMixin';
import { serialize, serializers, deserialize, deserializers } from '../helpers/SearchUrls';
import FareOnlySearchPreferences from './FareOnlySearchPreferences';
import { RAILCARD1617_CODE, JOB_CENTRE_PLUS_RAILCARD_CODE } from '../constants/AppConstants';
import PromoCode from './PromoCode';

const defaults = {
    origin: new Station(),
    destination: new Station(),
    dates: new CustomDuration()
        .setDuration({
            days: 11,
            months: 1
        }),
    passengerType: PassengerTypes.ADULT,
    flexi: FeatureFlagStore.get('enableFlexiSeasonTickets'),
    weekly: FeatureFlagStore.get('enableWeeklySeasonTickets'),
    fourWeekly: false,
    monthly: true,
    quarterly: false,
    sixMonthly: false,
    annual: true,
    custom: false,
    railcard1617: null,
    railcardJobCentrePlus: null,
    railcardCode: null,
    submitted: false,
    searchPreferences: new FareOnlySearchPreferences(),
    showAdditionalRoutes: false,
    promoCode: new PromoCode(),
    orderId: null,
    isRailcard1617Checked: false,
};

const ISO_WEEKEND_DAYS = [ 6, 7 ];


class SeasonSearch extends record(defaults) {

    serialize () {
        const { railcardJobCentrePlus } = this;

        const params = serialize({
            origin: serializers.station,
            destination: serializers.station,
            passengerType: serializers.identity,
            railcardCode: code => _.uniq([code, railcardJobCentrePlus ? JOB_CENTRE_PLUS_RAILCARD_CODE : null].filter(x => Boolean(x))).join(','),
            startDate: serializers.durationStartDate('dates'),
            duration: serializers.durations('dates'),
            promoCode: serializers.promoCode
        }, this);

        const querySchema = {
            searchPreferences: (preferences) => preferences.toString(),
            showAdditionalRoutes: serializers.boolean,
            promoCode: serializers.promoCode,
            isRailcard1617Checked: isRailcard1617Checked => isRailcard1617Checked,
        };

        const query = serialize(querySchema, this);

        return {
            params,
            query
        };
    }

    toFareOnlyJSON () {
        return serialize({
            origin: station => station.nlc,
            destination: station => station.nlc,
            outward: serializers.durationStartDate('dates'),
            adult: () => this.passengerType === PassengerTypes.ADULT,
            railcards: () => serializers.identity(this.railcardCode),
            searchPreferences: searchPreferences => searchPreferences.toJSON(),
            promoCode: (promoCode) => promoCode ? promoCode.promoCode : null,
        }, this);
    }

    isAdult () {
        return this.passengerType === PassengerTypes.ADULT;
    }

    validateHasDuration () {
        const durations = ['flexi', 'weekly', 'fourWeekly', 'monthly', 'quarterly', 'sixMonthly', 'annual', 'custom'];
        return _.compact(_.values(_.pick(this, durations))).length >= 1;
    }

    validateDatesInFuture () {
        return this.getStartDate() && this.getStartDate().isSameOrAfter(trainTime(), 'day');
    }

    validateDateLessThanMax () {
        const max = trainTime().add(trainTime.duration(
            config.get('maxSeasonStartPeriod')
        ));
        return this.getStartDate() && this.getStartDate().isSameOrBefore(max, 'day');
    }

    validateRailcard1617 () {
        const railcard1617 = this.railcard1617;

        if (railcard1617 && railcard1617.isPresent() && this.dates.getEndDate()) {
            const endDate = this.dates.getEndDate().clone();

            if (endDate.isSameOrAfter(railcard1617.getMaximumValidDate())) {
                return false;
            }
        }

        return true;
    }

    isAnnualAvailable () {
        if (this.railcard1617 && this.railcard1617.isPresent()) {
            const startDate = this.getStartDate() ? this.getStartDate().clone() : trainTime();
            const lastDayOfAnnualTicket = startDate.add({ years: 1 }).subtract({ days: 1 });

            return this.railcard1617.getMaximumValidDate().isSameOrAfter(lastDayOfAnnualTicket);
        }

        return true;
    }

    isValid () {
        return this.getValidationErrors().isEmpty();
    }

    getValidationErrors () {
        const tests = set([
            'validateStationsComplete',
            'validateStationsNotEqual',
            'validateHasDuration',
            'validateDatesInFuture',
            'validateDateLessThanMax',
            'validateRailcard1617'
        ]);

        return tests.filterNot((test) => this[test]());
    }

    refreshDates () {

        const now = trainTime();
        const dates = this.dates || defaults.dates;

        if (this.getStartDate()) {
            if (dates.getStartDate().isBefore(now)) {
                return this.set('dates', dates.setStartDate(trainTime().startOf('day')));
            }
        }

        return this;
    }

    hasPromoCode() {
        return !!this.getPromoCode();
    }

    getPromoCode () {
        return this.promoCode && this.promoCode.promoCode;
    }

    setPromoCode(promo) {
        return this.set('promoCode', promo);
    }

    isInFlexiLimbo() {
        // Temporary method. Can remove this & all related code after 28-June 2021
        return this.getStartDate().isBefore(trainTime('2021-06-28'));
    }

    getStartDate () {
        return this.dates.getStartDate();
    }

    setStartDate (date) {
        return this.set('dates', this.dates.setStartDate(date));
    }

    refreshFilters () {
        let search = this;
        if (this.disableFilters()) {
            search = this.merge({
                flexi: false,
                fourWeekly: false,
                monthly: false,
                annual: false,
                custom: false,
                sixMonthly: false,
                quarterly: false
            });
        }
        return search;
    }

    /**
     * Returns the next possible delivery date, taking into account weekends, from
     * a given moment in time.
     *
     * @param  {Moment} datetime
     * @return {Moment}
     */
    getNextDeliveryDateFrom (datetime) {
        let leadTimeInDays = config.get('minDeliveryLeadTimeInDays');
        let nextDeliveryDate = datetime.clone().startOf('day');

        if (datetime.hour() >= config.get('pivotalPostalHour')) {
            nextDeliveryDate = nextDeliveryDate.clone().add(1, 'day');
        }

        while (leadTimeInDays > 0) {
            nextDeliveryDate = nextDeliveryDate.clone().add(1, 'day');
            leadTimeInDays--;

            if (_.includes(ISO_WEEKEND_DAYS, nextDeliveryDate.isoWeekday())) {
                leadTimeInDays++; // Add leadtime for weekend days
            }
        }

        return nextDeliveryDate.clone();
    }

    /**
     * Returns the next possible start date for a season ticket, based on its type using
     * the duration of its validity. Monthly and annual season tickets must be posted to the
     * customer for revenue protection reasons - weekly season tickets can be collected via TOD.
     *
     * @param  {Moment.Duration} duration
     * @return {Moment}
     */
    getNextPossibleStartDate (duration) {
        if (duration.months() > 0 || duration.years() > 0) {
            return this.getNextDeliveryDateFrom(trainTime());
        } else {
            return trainTime().startOf('day').clone();
        }
    }

    disableFilters () {
        return trainTime().addWorkingDays(3) > this.getStartDate();
    }

    getMinDate() {
        return this.getNextDeliveryDateFrom(trainTime());
    }

    getMaxDate() {
        const maxPeriod = config.get('maxSeasonStartPeriod');

        return trainTime().add(trainTime.duration(maxPeriod));
    }

    getRouteOptions () {
        const options = config.get('journeySelectorOptions');
        const additionalOptions = config.get('journeySelectorAdditionalOptions');
        if (this.showAdditionalRoutes) {
            return options.concat(additionalOptions);
        }
        return options;
    }

    matchesRouteOptions() {
        // where options is a List of Lists which contain a pair of CRSs
        // (see journeySelectorOptions in config)
        return this.getRouteOptions().some(option =>
            option.get(0) === this.origin.crs && option.get(1) === this.destination.crs);
    }

    static deserialize (params, query) {
        const source = Object.assign({}, query, params);

        const { railcardCode } = source;

        const schema = {
            origin: deserializers.station,
            destination: deserializers.station,
            passengerType: deserializers.identity,
            railcardCode: () => railcardCode && railcardCode.split(',').includes(RAILCARD1617_CODE) ? { ['railcardCode']: RAILCARD1617_CODE } : null,
            duration: deserializers.durations,
            searchPreferences: (value, data, name) => {
                return { [name]: FareOnlySearchPreferences.fromString(value) };
            },
            showAdditionalRoutes: deserializers.boolean,
            railcardJobCentrePlus: () => railcardCode && railcardCode.split(',').includes(JOB_CENTRE_PLUS_RAILCARD_CODE) ? { ['railcardJobCentrePlus']: true } : null,
            promoCode: deserializers.promoCode
        };

        return deserialize(schema, source).then((deserialized) => {
            return new SeasonSearch(deserialized);
        });

    }

    static fromJSON (json = {}) {
        return construct(SeasonSearch, json, {
            origin: resolve.as(Station),
            destination: resolve.as(Station),
            passengerType: resolve.with(String),
            railcardCode: resolve.with(String),
            dates: resolve.as(CustomDuration),
            flexi: resolve.with(Boolean),
            weekly: resolve.with(Boolean),
            monthly: resolve.with(Boolean),
            annual: resolve.with(Boolean),
            custom: resolve.with(Boolean),
            submitted: resolve.with(Boolean),
            searchPreferences: resolve.as(FareOnlySearchPreferences),
            showAdditionalRoutes: resolve.with(Boolean)
        });
    }

    static fromFare(fare, date = null) {
        const validity = fare.getOutwardValidityPeriod();
        const custom = validity.isCustom();
        const match = validity.matchDurationType.bind(validity);
        const duration = validity.toDuration();
        if (!date) {
            const search = new SeasonSearch();
            date = search.getNextPossibleStartDate(duration);
        }
        let search = new SeasonSearch({
            origin: fare.origin,
            destination: fare.destination,
            dates: new CustomDuration({ startDate: date }).setDuration({ months: 1 }),
            passengerType: fare.children() ? PassengerTypes.CHILD : PassengerTypes.ADULT,
            weekly: match(DurationTypes.WEEKLY),
            fourWeekly: match(DurationTypes.FOUR_WEEKLY),
            monthly: match(DurationTypes.MONTHLY),
            quarterly: match(DurationTypes.QUARTERLY),
            sixMonthly: match(DurationTypes.SIX_MONTHLY),
            annual: match(DurationTypes.ANNUAL),
            custom: custom,
            searchPreferences: FareOnlySearchPreferences.fromFare(fare)
        });

        if (custom) {
            search = search.set('dates', search.dates.setDuration(duration));
        }

        return search;
    }

    /**
     * @param {Favourite} favourite
     * @param {trainTime} date
     * @return {SeasonSearch}
     */
    static fromFavourite(favourite, date = null) {
        const duration = favourite.getDuration();
        const validity = favourite.getOutwardValidityPeriod();
        const custom = validity.isCustom();
        const match = validity.matchDurationType.bind(validity);
        if (!date) {
            const search = new SeasonSearch();
            date = search.getNextPossibleStartDate(duration);
        }
        let search = new SeasonSearch({
            origin: favourite.origin,
            destination: favourite.destination,
            dates: new CustomDuration({ startDate: date }).setDuration({ months: 1 }),
            passengerType: favourite.children ? PassengerTypes.CHILD : PassengerTypes.ADULT,
            railcardCode: favourite.railcards.map(({ code }) => code),
            weekly: match(DurationTypes.WEEKLY),
            fourWeekly: match(DurationTypes.FOUR_WEEKLY),
            monthly: match(DurationTypes.MONTHLY),
            quarterly: match(DurationTypes.QUARTERLY),
            sixMonthly: match(DurationTypes.SIX_MONTHLY),
            annual: match(DurationTypes.ANNUAL),
            custom: custom,
            searchPreferences: FareOnlySearchPreferences.fromFavourite(favourite)
        });

        if (custom) {
            search = search.set('dates', search.dates.setDuration(duration));
        }

        return search;
    }

    static fromReorderable(reorderable, date = null) {
        if (reorderable instanceof OrderFare) {
            return SeasonSearch.fromFare(reorderable, date);
        } else if (reorderable instanceof Favourite) {
            return SeasonSearch.fromFavourite(reorderable, date);
        }

        return null;
    }

    getJsonForJourneyNotification () {
        return {
            origin: this.origin.nlc,
            destination: this.destination.nlc,
            startDate: this.getStartDate().format('YYYY-MM-DD HH:mm:ss'),
            adult: this.passengerType === 'adult',
            child: this.passengerType === 'child'
        };
    }
}

Object.assign(SeasonSearch.prototype, SearchMixin);

export default SeasonSearch;



// WEBPACK FOOTER //
// ./src/records/SeasonSearch.js